export const buildUserFormData = (user: any, family: any, tags: any, household: any) => {
  // Every text field is read with `?? ''` rather than handed over as-is. A key
  // the contact payload omits arrives as undefined, and an input given
  // value={undefined} is uncontrolled - React stops managing its text and the
  // previously loaded contact's value stays in the DOM. It also keeps the
  // fields out of JSON.stringify's blind spot: it drops undefined keys, so the
  // save button's dirty check compared two objects with different shapes.
  const text = (v: any) => v ?? ''

  let formData = {
    id: user?.userId ?? 0,
    // The contact's home community. The upsert procedure updates the global user
    // row (name, id number, birth date...) only when it is handed that community,
    // so the form has to know it to tell which fields a save can actually change.
    // Newtonsoft camel-cases DefaultCommunityID to defaultCommunityID; the second
    // spelling is only there so a rename on the server cannot silently unlock the
    // fields.
    defaultCommunityId: user?.defaultCommunityID ?? user?.defaultCommunityId ?? null,
    // Every community the contact belongs to, which is the set their primary
    // community can be moved within. Read as a list of ids, the shape User_Get
    // returns them in.
    communityIds: Array.isArray(user?.communityIds) ? user.communityIds : [],
    population: {
      firstName: text(user?.givenName),
      lastName: text(user?.familyName),
      middleName: text(user?.middleName),
      nickName: text(user?.nickName),
      identityNumber: text(user?.israeliId),
      city: text(user?.addressCity),
      street: text(user?.addressStreet),
      houseNumber: text(user?.addressHouseNumber),
      userTypes: tags ? tags.filter((t: any) => t.tagTypeId == 1).map((item: any) => ({ tagName: item.tag, tagTypeId: item.tagTypeId, action: "fromList" })) : [],
      nickname: text(user?.nickname),
      isDead: user ? user.dateOfDeath != null : false,
      birthDate: user?.dateOfBirth ?? null,
      isLocalMember: user ? (user.mekomeEnabled ?? false) : false,
      deathDate: user?.dateOfDeath ?? null,
      email: text(user?.email),
      phone: text(user?.mainMobilePhone),
      extraMobile: text(user?.secondaryMobilePhone),
      extraPhone: text(user?.otherPhone),
      gender: text(user?.gender),
      shelter: text(user?.shelter),
      maritalStatus: text(user?.maritalStatus),
    },
    emergency: {
      contacts: [] as any[]
    },
    family: {
      household: household ? {
        id: household.id,
        firstName: household.houseHoldName,
        createdAt: new Date(household.created)
      } : null,
      spouse: null as any,
      children: [] as any[]
    },
    contacts: {
      roles: tags ? tags.filter((t: any) => t.tagTypeId == 2).map((item: any) => ({ tagName: item.tag, tagTypeId: item.tagTypeId, action: "fromList" })) : [],
      tags: tags ? tags.filter((t: any) => t.tagTypeId == 3).map((item: any) => ({ tagName: item.tag, tagTypeId: item.tagTypeId, action: "fromList" })) : [],
      groups: [] as any[],
      permissions: {},
    },
  }

  if (user?.contactPerson1 || user?.contactPersonCellPhone1) {
    formData.emergency.contacts.push({
      mode: 'manual',
      name: text(user.contactPerson1),
      phone: text(user.contactPersonCellPhone1),
      address: ''
    })
  }

  if (user?.contactPerson2 || user?.contactPersonCellPhone2) {
    formData.emergency.contacts.push({
      mode: 'manual',
      name: text(user.contactPerson2),
      phone: text(user.contactPersonCellPhone2),
      address: ''
    })
  }

  if (family) {
    const parseMember = (relatedUserId: number) => {
      const member = family.familyMembers?.find((m: any) => m.userId === relatedUserId)
      if (!member) return null

      const [firstName, ...rest] = (member.fullName || '').split(' ')
      return {
        mode: 'select' as const,
        id: member.userId,
        firstName,
        lastName: rest.join(' '),
        phone: member.mainMobilePhone || '',
        identityNumber: member.israelId || '',
      }
    }

    const currentUserId = user.userId

    // Children (relationTypeId 2) is a directional parent -> child relation, so
    // only the people for whom the current user is the parent (the related side)
    // are the current user's children.
    const childIds = (family.relations || [])
      .filter((r: any) => currentUserId && r.relatedUserId !== currentUserId && r.relationTypeId === 2)
      .map((r: any) => r.relatedUserId)

    formData.family.children = childIds
      .map((id: any) => parseMember(id))
      .filter(Boolean)

    // Spouse (relationTypeId 3) is a *bidirectional* relation: the backend stores
    // a single record per pair and may store it in either direction. So the
    // counterpart is whichever endpoint isn't the current user — relatedUserId
    // normally, or the relation owner (userId) when the relation was stored the
    // other way round (current user is the relatedUserId). Falling back to the
    // family member that isn't the user or a child covers records that don't
    // carry the owner id. Without this, a spouse stored in the reverse direction
    // is silently hidden even though the backend reports it exists.
    if (currentUserId) {
      const spouseRelations = (family.relations || []).filter((r: any) => r.relationTypeId === 3)

      let spouseId = spouseRelations
        .map((r: any) => (r.relatedUserId && r.relatedUserId !== currentUserId ? r.relatedUserId : r.userId))
        .find((id: any) => id && id !== currentUserId)

      if (spouseRelations.length && !spouseId) {
        spouseId = (family.familyMembers || [])
          .find((m: any) => m.userId !== currentUserId && !childIds.includes(m.userId))?.userId
      }

      if (spouseId) {
        formData.family.spouse = parseMember(spouseId)
      }
    }
  }

  return formData
}
